import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatError, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, ShieldCheck, BookmarkSimple, Heart, ChatCircleText, EyeSlash, ProhibitInset,
  UserPlus, UserMinus, ChatTeardropDots, BookOpen, Trash, Star, ChatCircle, Cake, Calendar, Trophy,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  CollectionCard, RecentAchievements, SectionTitle, ProfileBanner,
  LevelSidebarCard, NextTitleCard, ConquistasSidebarCard, EstatisticasCard,
} from "@/components/ProfileWidgets";
import SocialLinks from "@/components/SocialLinks";
import FollowListDialog from "@/components/FollowListDialog";

export default function MemberDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [collection, setCollection] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [readList, setReadList] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followListType, setFollowListType] = useState(null);

  async function loadAll() {
    try {
      const { data } = await api.get(`/members/${id}`);
      setMember(data);
      setFollowing(data.am_following);
      if (!data.is_private) {
        Promise.all([
          api.get(`/members/${id}/manga`, { params: { list_type: "collection" } }).then(r => setCollection(r.data)).catch(() => {}),
          api.get(`/members/${id}/manga`, { params: { list_type: "wishlist" } }).then(r => setWishlist(r.data)).catch(() => {}),
          api.get(`/members/${id}/manga`, { params: { list_type: "read" } }).then(r => setReadList(r.data)).catch(() => {}),
          api.get(`/members/${id}/progress`).then(r => setProgress(r.data)).catch(() => {}),
        ]);
      }
    } catch (e) {
      toast.error("Membro não encontrado");
      navigate("/membros");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { setLoading(true); loadAll(); /* eslint-disable-next-line */ }, [id]);

  async function toggleFollow() {
    if (!user) { navigate("/login"); return; }
    try {
      if (following) {
        await api.delete(`/me/follow/${member.user_id}`);
        setFollowing(false);
        setMember({...member, follower_count: Math.max(0, member.follower_count - 1)});
      } else {
        await api.post(`/me/follow/${member.user_id}`);
        setFollowing(true);
        setMember({...member, follower_count: member.follower_count + 1});
      }
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  async function deleteAccount() {
    if (!confirm(`Apagar a conta de "${member.name}" PERMANENTEMENTE? Esta acção não pode ser revertida.`)) return;
    try {
      await api.delete(`/admin/users/${member.user_id}`);
      toast.success("Conta apagada");
      navigate("/membros");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  if (loading) return <div className="pt-32 text-center text-zinc-500 font-mono-accent text-xs">A CARREGAR...</div>;
  if (!member) return null;

  const isBanned = member.banned_until && (member.banned_until === "permanent" || new Date(member.banned_until) > new Date());
  let banLabel = "";
  if (isBanned) {
    if (member.banned_until === "permanent") banLabel = "BANIDO PERMANENTEMENTE";
    else {
      const dt = new Date(member.banned_until);
      banLabel = `BANIDO ATÉ ${dt.toLocaleDateString("pt-PT")} ${dt.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}`;
    }
  }

  return (
    <div className="pt-16 pb-24 max-w-6xl mx-auto px-3 lg:px-6" data-testid="member-detail-page">
      <Link to="/membros" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-4 pt-4">
        <ArrowLeft size={16} weight="bold" /> Voltar aos membros
      </Link>

      {/* ============ BANNER ============ */}
      <ProfileBanner bannerUrl={member.banner_url} />

      {/* ============ IDENTITY BAR ============ */}
      <div className="relative -mt-16 md:-mt-20 px-2 md:px-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-end gap-5">
          <div className="flex-shrink-0">
            {member.picture ? (
              <img src={resolveImageUrl(member.picture)} alt="" className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-[#0a0a0f] bg-[#0a0a0f]" />
            ) : (
              <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-5xl font-display font-bold border-4 border-[#0a0a0f]">
                {(member.name || "?")[0]?.toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 pb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight" data-testid="member-name">{member.name || "Sem nome"}</h1>
              {member.role === "admin" && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#E63946] text-white text-[10px] font-mono-accent font-bold tracking-wider">
                  <ShieldCheck size={12} weight="fill" /> ADMIN
                </span>
              )}
              {member.is_private && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-zinc-700/40 text-zinc-300 text-xs font-mono-accent">
                  <EyeSlash size={12} weight="bold" /> PRIVADO
                </span>
              )}
              {isBanned && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/15 text-red-400 text-xs font-mono-accent">
                  <ProhibitInset size={12} weight="fill" /> {banLabel}
                </span>
              )}
              {!!member.level && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gradient-to-r from-[#E63946]/20 to-[#7209B7]/20 border border-[#E63946]/30 text-white text-xs font-mono-accent" data-testid="member-level-badge">
                  NÍVEL {member.level}
                </span>
              )}
            </div>
            <div className="text-sm text-zinc-400 mt-1">
              Membro desde {new Date(member.created_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "long", year: "numeric" })}
            </div>
            {member.bio && <p className="mt-2 text-zinc-300 leading-relaxed max-w-2xl whitespace-pre-line text-sm">{member.bio}</p>}
            <div className="mt-3 flex items-center gap-4 text-sm">
              <button type="button" onClick={() => setFollowListType("followers")} className="hover:text-white transition-colors" data-testid="member-followers-btn">
                <span className="font-display font-bold text-white">{member.follower_count || 0}</span> <span className="text-zinc-500 hover:text-zinc-300">seguidores</span>
              </button>
              <span className="text-zinc-700">·</span>
              <button type="button" onClick={() => setFollowListType("following")} className="hover:text-white transition-colors" data-testid="member-following-btn">
                <span className="font-display font-bold text-white">{member.following_count || 0}</span> <span className="text-zinc-500 hover:text-zinc-300">a seguir</span>
              </button>
            </div>
            <SocialLinks user={member} className="mt-3" />

          </div>

          {!member.is_me && user && (
            <div className="flex flex-col gap-2 min-w-[180px] pb-2">
              <Button onClick={toggleFollow} className={following ? "bg-zinc-800 hover:bg-zinc-700 text-white" : "btn-crimson"} data-testid="follow-btn">
                {following ? <><UserMinus size={16} weight="bold" className="mr-2" /> A seguir</> : <><UserPlus size={16} weight="bold" className="mr-2" /> Seguir</>}
              </Button>
              <Button variant="outline" onClick={() => navigate(`/mensagens/${member.user_id}`)} className="border-white/15 text-white hover:bg-white/5" data-testid="message-btn">
                <ChatTeardropDots size={16} weight="bold" className="mr-2" /> Mensagem
              </Button>
              {user.role === "admin" && member.role !== "admin" && (
                <Button variant="outline" onClick={deleteAccount} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="admin-delete-user-btn">
                  <Trash size={14} weight="bold" className="mr-2" /> Apagar conta
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ============ PRIVATE FALLBACK ============ */}
      {member.is_private ? (
        <div className="border border-dashed border-white/10 rounded-2xl p-12 text-center">
          <EyeSlash size={48} weight="duotone" className="text-zinc-500 mx-auto mb-3" />
          <div className="font-display font-bold text-xl">Perfil Privado</div>
          <div className="text-sm text-zinc-500 mt-1">Este membro decidiu manter o perfil privado.</div>
        </div>
      ) : (
        <>
          {/* ============ 2-COLUMN LAYOUT ============ */}
          <div className="grid lg:grid-cols-[280px_1fr] gap-5">
            <aside className="space-y-4">
              {progress && <LevelSidebarCard progress={progress} />}
              <NextTitleCard user={member} />
              {progress && <ConquistasSidebarCard progress={progress} />}
              <EstatisticasCard items={[
                { icon: <BookmarkSimple size={14} weight="fill" />, label: "Coleção", value: member.stats?.collection ?? 0 },
                { icon: <Heart size={14} weight="fill" />, label: "Desejos", value: member.stats?.wishlist ?? 0 },
                { icon: <BookOpen size={14} weight="fill" />, label: "Lidos", value: member.stats?.read ?? 0 },
                { icon: <ChatCircleText size={14} weight="fill" />, label: "Tópicos", value: member.stats?.topics ?? 0 },
                { icon: <Star size={14} weight="fill" />, label: "Avaliações", value: progress?.counters.ratings ?? 0 },
                { icon: <ChatCircle size={14} weight="fill" />, label: "Comentários", value: progress?.counters.comments ?? 0 },
                { icon: <Heart size={14} weight="fill" />, label: "Likes em eventos", value: progress?.counters.event_likes ?? 0 },
                { icon: <Cake size={14} weight="fill" />, label: "Dias como membro", value: progress?.counters.membership_days ?? 0 },
              ]} />
            </aside>

            <div className="space-y-6">
              <div>
                <SectionTitle title="COLEÇÃO DO MEMBRO" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <CollectionCard title="Comprados" icon={<BookmarkSimple size={18} weight="fill" />} color="#E63946" items={collection} to={`/membros/${id}/lista/collection`} />
                  <CollectionCard title="Desejo Comprar" icon={<Heart size={18} weight="fill" />} color="#7209B7" items={wishlist} to={`/membros/${id}/lista/wishlist`} />
                  <CollectionCard title="Já Lidos" icon={<BookOpen size={18} weight="fill" />} color="#10B981" items={readList} to={`/membros/${id}/lista/read`} />
                </div>
              </div>
            </div>
          </div>

          {/* ============ RECENT ACHIEVEMENTS (full width) ============ */}
          {progress && <div className="mt-8"><RecentAchievements achievements={progress.achievements} /></div>}
        </>
      )}
      <FollowListDialog
        open={!!followListType}
        onOpenChange={(v) => { if (!v) setFollowListType(null); }}
        identifier={member.user_id}
        type={followListType || "followers"}
      />
    </div>
  );
}
